import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate } from '@tanstack/react-router';
import { isEqual } from 'lodash';
import { useEffect } from 'react';
import { useForm, Controller, useWatch } from 'react-hook-form';
import { z } from 'zod';

import { useTabbar } from '@common/providers/useTabbar';
import { BottomSheet } from '@common/ui/BottomSheet';
import { Button } from '@common/ui/buttons/Button';
import { Cell } from '@common/ui/Cell';
import { Input } from '@common/ui/inputs/Input';
import { MultiSelect } from '@common/ui/selection';
import { Separator } from '@common/ui/Separator';
import { Toogle } from '@common/ui/Toogle';
import { Paragraph } from '@common/ui/typography/Paragraph';

import { useAuthStore } from '@entities/auth/stores/useAuthStore';
import { dicts } from '@entities/dating/dicts/filters';
import {
  useFiltersStore,
  defaultFilters,
} from '@entities/dating/stores/useFiltersStore';
import { PremiumUpsell } from '@entities/premium';
import { type ZodiacSign } from '@entities/profile/types';

import styles from './Filter.module.scss';

const filtersSchema = z.object({
  age: z
    .object({
      from: z
        .number()
        .min(18, { message: 'Минимальный возраст 18' })
        .max(80, { message: 'Максимальный возраст 80' })
        .nullable(),
      to: z
        .number()
        .min(18, { message: 'Минимальный возраст 18' })
        .max(80, { message: 'Максимальный возраст 80' })
        .nullable(),
    })
    .nullable()
    .refine(
      (val) =>
        !val || val.from === null || val.to === null || val.from <= val.to,
      {
        message: '«От» больше «До»',
        path: ['from'],
      },
    ),
  hasAbout: z.boolean().nullable(),
  isVerified: z.boolean().nullable(),
  goals: z.array(z.string()).nullable(),
  alcoholViews: z.array(z.string()).nullable(),
  smokingViews: z.array(z.string()).nullable(),
  hasChildren: z.array(z.string()).nullable(),
  zodiac: z.array(z.string()).nullable() as z.ZodType<ZodiacSign[] | null>,
  height: z
    .object({
      from: z
        .number()
        .min(120, { message: 'Минимальный рост 120' })
        .max(230, { message: 'Максимальный рост 230' })
        .nullable(),
      to: z
        .number()
        .min(120, { message: 'Минимальный рост 120' })
        .max(230, { message: 'Максимальный рост 230' })
        .nullable(),
    })
    .nullable()
    .refine(
      (val) =>
        !val || val.from === null || val.to === null || val.from <= val.to,
      {
        message: '«От» больше «До»',
        path: ['from'],
      },
    ),
});

type FormData = z.infer<typeof filtersSchema>;

export const Filters = () => {
  const tabbar = useTabbar();
  const navigate = useNavigate();

  const { filters, setFilters } = useFiltersStore();
  const { user } = useAuthStore();

  const {
    control,
    reset,
    getValues,
    trigger,
    formState: { errors, isValid, isDirty },
  } = useForm<FormData>({
    values: filters,
    resolver: zodResolver(filtersSchema),
    mode: 'onChange',
  });

  useEffect(() => {
    trigger();
  }, []);

  const watchFields = useWatch({ control: control });

  const onSubmit = (data: FormData) => {
    setFilters(data);
    navigate({ to: '/dating' });
  };

  const onReset = () => {
    reset(defaultFilters, { keepDirty: false });
    setFilters(defaultFilters);
  };

  useEffect(() => {
    const buttons: React.ReactNode[] = [];

    // has edited
    if (!isEqual(watchFields, filters) && isValid && isDirty) {
      buttons.push(
        <Button
          testId="filters-apply-button"
          variant="actionPrimary"
          onClick={() => onSubmit(getValues())}
        >
          Применить
        </Button>,
      );
    }

    // has changed
    if (!isEqual(watchFields, defaultFilters)) {
      buttons.push(
        <Button
          testId="filters-reset-button"
          variant="actionSecondary"
          onClick={onReset}
        >
          Сбросить
        </Button>,
      );
    }

    if (buttons.length > 0) {
      tabbar.setBottomSheet(<BottomSheet buttons={buttons} />);
    } else {
      tabbar.setBottomSheet(undefined);
    }
  }, [watchFields, isValid, isDirty]);

  return (
    <form
      style={{
        display: 'flex',
        flexDirection: 'column',
        gridGap: '12px',
        minHeight: 'calc(100vh - 32px)',
      }}
    >
      <Paragraph compact noPadding variant="secondary">
        Возраст
      </Paragraph>
      <div style={{ display: 'flex', gridGap: '12px' }}>
        <Controller
          control={control}
          name="age.from"
          render={({ field }) => (
            <Input
              placeholder="От"
              value={field.value ?? ''}
              onChange={(e) => field.onChange(Number(e.target.value) || null)}
              error={errors.age?.from?.message}
              inputMode="numeric"
              regexp={/^[0-9]*$/}
            />
          )}
        />
        <Controller
          control={control}
          name="age.to"
          render={({ field }) => (
            <Input
              data-test-id="filters-age-to-input"
              placeholder="До"
              value={field.value ?? ''}
              onChange={(e) => {
                field.onChange(Number(e.target.value) || null);
                trigger('age.from');
              }}
              error={errors.age?.to?.message}
              inputMode="numeric"
              regexp={/^[0-9]*$/}
            />
          )}
        />
      </div>

      {!user.is_premium && (
        <PremiumUpsell
          title="Расширенные фильтры"
          subtitle="Premium даст вам больше шансов найти свой мэтч"
          button={
            <Button
              testId="filters-premium-button"
              variant="premiumGradient"
              className={styles.premiumBtn}
            >
              Приобрести Premium
            </Button>
          }
        />
      )}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gridGap: '12px',
          minHeight: 'calc(100vh - 32px)',
          opacity: user.is_premium ? 1 : 0.5,
          pointerEvents: user.is_premium ? 'auto' : 'none',
        }}
      >
        <Controller
          control={control}
          name="hasAbout"
          render={({ field }) => (
            <Cell
              testId="filters-has-about"
              title="Есть описание"
              suffixIcon={
                <Toogle
                  testId="filters-has-about-toogle"
                  checked={field.value || undefined}
                  onChange={(value) => field.onChange(value || null)}
                />
              }
              separator={<Separator />}
            />
          )}
        />
        <Controller
          control={control}
          name="isVerified"
          render={({ field }) => (
            <Cell
              testId="filters-is-verified"
              title="Подтвержденные профили"
              suffixIcon={
                <Toogle
                  testId="filters-is-verified-toogle"
                  checked={field.value || undefined}
                  onChange={(value) => field.onChange(value || null)}
                />
              }
              separator={<Separator />}
            />
          )}
        />
        <Controller
          control={control}
          name="goals"
          render={({ field }) => (
            <MultiSelect
              title="Цель"
              separator={<Separator />}
              items={dicts.goals}
              value={field.value || undefined}
              onChange={(value) =>
                value.length ? field.onChange(value) : field.onChange(null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="alcoholViews"
          render={({ field }) => (
            <MultiSelect
              title="Взгляды на алкоголь"
              separator={<Separator />}
              items={dicts.alcoholViews}
              value={field.value || undefined}
              onChange={(value) =>
                value.length ? field.onChange(value) : field.onChange(null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="smokingViews"
          render={({ field }) => (
            <MultiSelect
              title="Курит или не курит"
              separator={<Separator />}
              items={dicts.smokingViews}
              value={field.value || undefined}
              onChange={(value) =>
                value.length ? field.onChange(value) : field.onChange(null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="hasChildren"
          render={({ field }) => (
            <MultiSelect
              title="Наличие детей"
              separator={<Separator />}
              items={dicts.hasChildren}
              value={field.value || undefined}
              onChange={(value) =>
                value.length ? field.onChange(value) : field.onChange(null)
              }
            />
          )}
        />
        <Controller
          control={control}
          name="zodiac"
          render={({ field }) => (
            <MultiSelect
              title="Знак зодиака"
              items={dicts.zodiac}
              value={field.value || undefined}
              onChange={(value) =>
                value.length ? field.onChange(value) : field.onChange(null)
              }
            />
          )}
        />
        <Paragraph compact noPadding variant="secondary">
          Рост
        </Paragraph>
        <div style={{ display: 'flex', gridGap: '12px' }}>
          <Controller
            control={control}
            name="height.from"
            render={({ field }) => (
              <Input
                placeholder="От"
                value={field.value ?? ''}
                onChange={(e) => field.onChange(Number(e.target.value) || null)}
                error={errors.height?.from?.message}
                inputMode="numeric"
                regexp={/^[0-9]*$/}
              />
            )}
          />
          <Controller
            control={control}
            name="height.to"
            render={({ field }) => (
              <Input
                data-test-id="filters-height-to-input"
                placeholder="До"
                value={field.value ?? ''}
                onChange={(e) => {
                  field.onChange(Number(e.target.value) || null);
                  trigger('height.from');
                }}
                error={errors.height?.to?.message}
                inputMode="numeric"
                regexp={/^[0-9]*$/}
              />
            )}
          />
        </div>
      </div>
    </form>
  );
};
